import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortvilleRooms } from '../../../hooks/admin/usePortvilleRooms';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { useAuth } from '../../../hooks/useAuth';
import { PortvilleRoomWithResidency, ROOM_STATUS_LABELS, ROOM_STATUS_COLORS } from '../../../types/portvilleRoom';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';
import { downloadStorageFile } from '../../../utils/storageDownload';

const RoomDetailPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const { allRooms, loading } = usePortvilleRooms();
  const [room, setRoom] = useState<PortvilleRoomWithResidency | null>(null);
  const [downloadingRoommateIndex, setDownloadingRoommateIndex] = useState<number | null>(null);

  useBackToPortvilleRoomsListOnPop();

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || !hasAccess)) {
      navigate('/admin/dashboard');
      return;
    }
  }, [authLoading, user, hasAccess, navigate]);

  useEffect(() => {
    if (roomId && allRooms.length > 0) {
      const foundRoom = allRooms.find(r => r.id === roomId);
      setRoom(foundRoom || null);
    }
  }, [roomId, allRooms]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR');
  };

  const getDaysUntilExpiry = (endDate: Date) => {
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleViewContract = (contractType: 'original' | 'extension1' | 'extension2') => {
    if (room) {
      navigate(`/admin/portville-rooms/view-contract/${room.id}?type=${contractType}`);
    }
  };

  const handleDownloadRoommateAttachment = async (index: number) => {
    const attachment = room?.roommates?.[index]?.attachment;
    if (!attachment || !room) return;

    try {
      setDownloadingRoommateIndex(index);
      await downloadStorageFile(
        attachment.storagePath,
        `${room.roomNumber}호_동거인${index + 1}_${attachment.name.replace(/\.[^/.]+$/, '')}`
      );
    } catch (error) {
      console.error('동거인 첨부파일 다운로드 실패:', error);
      window.alert('동거인 첨부파일 다운로드에 실패했습니다.');
    } finally {
      setDownloadingRoommateIndex(null);
    }
  };

  const handleEdit = () => {
    if (room) {
      navigate(`/admin/portville-rooms/edit/${room.id}`);
    }
  };

  const handleBack = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  if (authLoading || loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
                호실 상세 정보
              </div>
              <button
                onClick={handleBack}
                className="w-[130px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
              >
                목록으로
              </button>
            </div>
            <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
          </div>

          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200 p-8">
            <div className="text-center text-gray-500">
              해당 호실을 찾을 수 없습니다.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusColor = ROOM_STATUS_COLORS[room.status as keyof typeof ROOM_STATUS_COLORS] || 'gray';
  const daysUntilExpiry = room.occupancyPeriod?.endDate ? getDaysUntilExpiry(room.occupancyPeriod.endDate) : null;

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              호실 상세 정보
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleEdit}
                className="w-[130px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
              >
                수정
              </button>
              <button
                onClick={handleBack}
                className="w-[130px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
              >
                목록으로
              </button>
            </div>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 기본 정보 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              기본 정보
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">호실번호</label>
                <div className="text-lg font-semibold text-gray-900">{room.roomNumber}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">방 상태</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${statusColor}-100 text-${statusColor}-800`}>
                  {ROOM_STATUS_LABELS[room.status as keyof typeof ROOM_STATUS_LABELS]}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 입주자 정보 (입주중이거나 계약진행인 경우) */}
        {(room.status === 'occupied' || room.status === 'contracting') && room.currentResident && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                {room.status === 'occupied' ? '입주자 정보' : '예정 입주자 정보'}
              </h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">성명</label>
                  <div className="text-gray-900">{room.currentResident.name}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">연락처</label>
                  <div className="text-gray-900">{room.currentResident.phoneNumber || '-'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">소속</label>
                  <div className="text-gray-900">{room.currentResident.organization || '-'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 입주기간 (입주중인 경우) */}
        {room.status === 'occupied' && room.occupancyPeriod && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">입주기간</h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">입주 개시일</label>
                  <div className="text-gray-900">{formatDate(room.occupancyPeriod.startDate)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">계약 종료일</label>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-900">{formatDate(room.occupancyPeriod.endDate)}</span>
                    {daysUntilExpiry !== null && (
                      <span className={`text-sm px-2 py-1 rounded ${
                        daysUntilExpiry <= 0 ? 'bg-red-100 text-red-700' :
                        daysUntilExpiry <= 7 ? 'bg-orange-100 text-orange-700' :
                        daysUntilExpiry <= 30 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {daysUntilExpiry <= 0 ? '계약 만료' : `${daysUntilExpiry}일 남음`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 추가 정보 */}
        {((room.roommates?.length || 0) > 0 || room.vehicleInfo || room.notes) && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">추가 정보</h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

              <div className="space-y-6">
                {(room.roommates || []).map((roommate, index) => (
                  <div key={index} className="rounded-md border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-3 text-sm font-semibold text-gray-900">동거인 {index + 1}</div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-xs text-gray-500">성명</div>
                        <div className="mt-1 text-gray-900">{roommate.name}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">연락처</div>
                        <div className="mt-1 text-gray-900">{roommate.contact}</div>
                      </div>
                    </div>
                    {roommate.attachment && (
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3">
                        <div className="min-w-0 truncate text-sm text-gray-700">
                          {roommate.attachment.name}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDownloadRoommateAttachment(index)}
                          disabled={downloadingRoommateIndex === index}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                        >
                          {downloadingRoommateIndex === index ? '다운로드 중...' : '다운로드'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {room.vehicleInfo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">차량정보</label>
                    <div className="text-gray-900 bg-gray-50 p-3 rounded border">{room.vehicleInfo}</div>
                  </div>
                )}
                {room.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">비고</label>
                    <div className="text-gray-900 bg-gray-50 p-3 rounded border">{room.notes}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 계약서 정보 */}
        {room.contractSignedAt && (
          <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-green-800">계약 완료</h3>
                <p className="text-sm text-green-600">
                  {formatDate(room.contractSignedAt)}에 계약이 완료되었습니다.
                </p>
              </div>
              <button
                onClick={() => handleViewContract('original')}
                className="px-4 py-2 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
              >
                계약서 보기
              </button>
            </div>
          </div>
        )}

        {/* 1차 연장계약서 정보 */}
        {room.extensionContractSignedAt && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-blue-800">1차 연장계약 완료</h3>
                <p className="text-sm text-blue-600">
                  {formatDate(room.extensionContractSignedAt)}에 연장계약이 완료되었습니다.
                </p>
                {room.originalEndDate && (
                  <p className="text-xs text-blue-500 mt-1">
                    원래 계약 종료일: {formatDate(room.originalEndDate)}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleViewContract('extension1')}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50"
              >
                1차 연장계약서 보기
              </button>
            </div>
          </div>
        )}

        {/* 2차 연장계약서 정보 */}
        {room.secondExtensionContractSignedAt && (
          <div className="mt-8 bg-purple-50 border border-purple-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-purple-800">2차 연장계약 완료</h3>
                <p className="text-sm text-purple-600">
                  {formatDate(room.secondExtensionContractSignedAt)}에 2차 연장계약이 완료되었습니다.
                </p>
                {room.secondOriginalEndDate && (
                  <p className="text-xs text-purple-500 mt-1">
                    직전(1차 연장 후) 계약 종료일: {formatDate(room.secondOriginalEndDate)}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleViewContract('extension2')}
                className="px-4 py-2 text-sm font-medium text-purple-700 bg-white border border-purple-300 rounded-md hover:bg-purple-50"
              >
                2차 연장계약서 보기
              </button>
            </div>
          </div>
        )}

        {/* 퇴거 승인 정보 */}
        {room.moveOutInfo && (
          <div className="mt-8 bg-orange-50 border border-orange-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-orange-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-medium text-orange-800">퇴거 승인됨 - 계약 종료일 변경</h3>
              </div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                계약 종료일 변경됨
              </span>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-orange-700">원래 계약 종료일</p>
                  <p className="text-sm text-orange-600">
                    {formatDate(room.moveOutInfo.originalEndDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-orange-700">변경된 계약 종료일</p>
                  <p className="text-sm text-orange-600 font-semibold">
                    {formatDate(room.moveOutInfo.approvedMoveOutDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-orange-700">퇴거 사유</p>
                  <p className="text-sm text-orange-600">
                    {room.moveOutInfo.moveOutReason === 'personnel_change' ? '인사변경' : '개인사유'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-orange-700">승인일</p>
                  <p className="text-sm text-orange-600">
                    {formatDate(room.moveOutInfo.approvedAt)}
                  </p>
                </div>
              </div>

              {room.moveOutInfo.notes && (
                <div className="pt-4 border-t border-orange-200">
                  <p className="text-sm font-medium text-orange-700">승인 메모</p>
                  <p className="text-sm text-orange-600 bg-white p-3 rounded border mt-2">
                    {room.moveOutInfo.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default RoomDetailPage;
